import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMasterEvent } from '../../context/MasterEventContext';
import { Link, useParams } from 'react-router-dom';
import { isActivityObfuscated } from '../../services/activityVisibility';
import { COMPETITIONS, MARKET_TYPES } from '../../data/mockData';
import { useBetting } from '../../context/BettingContext';
import Avatar from '../shared/Avatar';
import HelpTip from '../shared/HelpTip';
import './Admin.css';

// ==================== EVENTS & ROUNDS TAB ====================

function EventsTab() {
  const { api: apiService, events, rounds, markets, refreshData } = useBetting();
  const [showCreate, setShowCreate] = useState(false);
  const [eventName, setEventName] = useState('');
  const [totalRounds, setTotalRounds] = useState('3');
  const [startingCredits, setStartingCredits] = useState('1000');
  const [roundNames, setRoundNames] = useState([]);
  const [newRoundName, setNewRoundName] = useState('');
  const [addingRoundFor, setAddingRoundFor] = useState(null);
  const [expandedRounds, setExpandedRounds] = useState(new Set());
  const [acting, setActing] = useState(null);
  const [message, setMessage] = useState('');

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const numRounds = Math.max(1, parseInt(totalRounds, 10) || 1);
  useEffect(() => {
    setRoundNames((prev) => {
      const next = [...prev];
      while (next.length < numRounds) next.push('');
      return next.slice(0, numRounds);
    });
  }, [numRounds]);

  const toggleRound = (roundId) => {
    setExpandedRounds((prev) => {
      const next = new Set(prev);
      next.has(roundId) ? next.delete(roundId) : next.add(roundId);
      return next;
    });
  };

  const handleCreateEvent = async () => {
    if (!eventName.trim()) return;
    const names = roundNames.map((n, i) => n.trim() || `Round ${i + 1}`);
    await apiService.createEvent({ name: eventName.trim(), totalRounds: numRounds, startingCredits: parseInt(startingCredits, 10) || 1000, roundNames: names });
    setEventName(''); setTotalRounds('3'); setStartingCredits('1000'); setRoundNames([]); setShowCreate(false);
    await refreshData();
    flash('Event created with all rounds! Users credited.');
  };

  const handleCreateRound = async (eventId) => {
    if (!newRoundName.trim()) return;
    await apiService.createRound({ eventId, name: newRoundName.trim() });
    setNewRoundName(''); setAddingRoundFor(null);
    await refreshData();
    flash('Round added!');
  };

  const act = async (fn) => {
    try { await fn(); await refreshData(); return true; } catch (err) { flash(`Error: ${err.message}`); return false; }
  };

  const handleDeleteEvent = async (eventId, name) => {
    if (!window.confirm(`Delete "${name}" and ALL its rounds, markets, and bets? This cannot be undone.`)) return;
    setActing(eventId);
    if (await act(() => apiService.deleteEvent(eventId))) flash('Event deleted.');
    setActing(null);
  };

  const handleDeleteRound = async (roundId, name) => {
    if (!window.confirm(`Delete "${name}" and all its markets/bets?`)) return;
    setActing(roundId);
    if (await act(() => apiService.deleteRound(roundId))) flash('Round deleted.');
    setActing(null);
  };

  const handleRoundAction = async (action, roundId) => {
    setActing(roundId);
    await act(async () => {
      if (action === 'open') await apiService.openRound(roundId);
      else if (action === 'close') await apiService.closeRound(roundId);
      else if (action === 'conclude-carry') await apiService.concludeRound(roundId, true);
      else if (action === 'conclude-reset') await apiService.concludeRound(roundId, false);
      else if (action === 'reopen') await apiService.reopenRound(roundId);
    });
    setActing(null);
  };

  const handleConcludeEvent = async (eventId, carryOver) => {
    setActing(eventId);
    if (await act(() => apiService.concludeEvent(eventId, carryOver))) flash('Event concluded. Final balances and activity scores recorded.');
    setActing(null);
  };

  const handleSettleMarket = async (marketId, winningOption) => {
    setActing(marketId);
    await act(() => apiService.settleMarket(marketId, winningOption));
    setActing(null);
  };

  const handleReopenEvent = async eventId => {
    setActing(eventId);
    if (await act(() => apiService.reopenEvent(eventId))) flash('Event reopened. Reopen a round or market to resume betting. Final scores will be recalculated when concluded.');
    setActing(null);
  };

  const handleReopenMarket = async market => {
    if (market.status === 'settled' && !window.confirm(`Reopen "${market.matchName}"? Its existing bets become pending, paid winnings are reversed, and final scores must be concluded again.`)) return;
    setActing(market.id);
    if (await act(() => apiService.reopenMarket(market.id))) flash('Market reopened. Existing bets are pending. If its deadline has passed, close it manually when ready.');
    setActing(null);
  };

  const handleDeleteMarket = async (marketId, name) => {
    if (!window.confirm(`Delete market "${name}"?`)) return;
    setActing(marketId);
    await act(() => apiService.deleteMarket(marketId));
    setActing(null);
  };

  return (
    <div>
      <div className="admin-card-header" style={{ marginBottom: 16 }}>
        <h3>Events & Rounds</h3>
        <button className="admin-refresh-btn" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? '✕ Cancel' : '+ New Event'}
        </button>
      </div>

      {message && <div className="admin-success" style={{ marginBottom: 16 }}>{message}</div>}

      {showCreate && (
        <div className="admin-card" style={{ marginBottom: 20 }}>
          <h4 className="admin-section-title">Create New Event</h4>
          <div className="admin-form-grid">
            <div className="admin-form-field"><label>Event Name</label><input type="text" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Spring Championship 2026" /></div>
            <div className="admin-form-field"><label>Number of Rounds<HelpTip label="Number of rounds">Rounds group markets (e.g. matchdays). Only the first round opens at the start; open the next one from the round list when ready.</HelpTip></label><input type="number" value={totalRounds} onChange={(e) => setTotalRounds(e.target.value)} min="1" max="20" /></div>
            <div className="admin-form-field"><label>Starting Credits<HelpTip label="Starting credits">Coins each player (or each team wallet, in a team activity) starts this event with. You can switch to points from earlier competitions, or adjust single teams, in the Balances tab.</HelpTip></label><input type="number" value={startingCredits} onChange={(e) => setStartingCredits(e.target.value)} min="0" /></div>
          </div>
          <div className="admin-round-names">
            <h4 className="admin-section-title" style={{ marginTop: 16 }}>Name Your Rounds</h4>
            {roundNames.map((name, i) => (
              <div key={i} className="admin-round-name-row">
                <span className="admin-round-name-label">R{i + 1}</span>
                <input type="text" value={name} onChange={(e) => { const next = [...roundNames]; next[i] = e.target.value; setRoundNames(next); }} placeholder={`Round ${i + 1}`} className="admin-round-name-input" />
              </div>
            ))}
          </div>
          <button className="admin-primary-btn" onClick={handleCreateEvent} disabled={!eventName.trim()}>Create Event ({numRounds} rounds)</button>
        </div>
      )}

      {events.length === 0 ? (
        <p className="admin-empty">No events yet. Create one to get started!</p>
      ) : (
        events.map((event) => {
          const evtRounds = rounds.filter((r) => r.eventId === event.id).sort((a, b) => a.roundNumber - b.roundNumber);
          const isActive = event.status === 'active';

          return (
            <div key={event.id} className={`admin-card ${!isActive ? 'admin-card--muted' : ''}`} style={{ marginBottom: 16 }}>
              <div className="admin-event-header">
                <div>
                  <span className={`admin-event-badge ${isActive ? 'admin-event-badge--active' : 'admin-event-badge--concluded'}`}>
                    {isActive ? 'Active' : 'Concluded'}
                  </span>
                  <h4 className="admin-event-name">{event.name}</h4>
                  <span className="admin-event-meta">{evtRounds.length} rounds — {event.startingCredits?.toLocaleString()} credits</span>
                </div>
                <div className="admin-event-actions">
                  {isActive && (
                    <>
                      <HelpTip label="Concluding an event">Every market must be settled first. Concluding ranks everyone by balance and awards activity points from the placement config. Carry keeps balances for the next event; reset starts the next event fresh.</HelpTip>
                      <button className="admin-conclude-btn" onClick={() => handleConcludeEvent(event.id, true)} disabled={acting === event.id}>Conclude (carry)</button>
                      <button className="admin-conclude-btn admin-conclude-btn--reset" onClick={() => handleConcludeEvent(event.id, false)} disabled={acting === event.id}>Conclude (reset)</button>
                    </>
                  )}
                  {!isActive && <button className="admin-action-btn admin-action-btn--open" onClick={() => handleReopenEvent(event.id)} disabled={acting !== null}>Reopen event</button>}
                  <button className="admin-delete-btn" onClick={() => handleDeleteEvent(event.id, event.name)} disabled={acting === event.id} title="Delete event">🗑️</button>
                </div>
              </div>

              {/* Rounds with expandable markets */}
              <div className="admin-rounds-list">
                {evtRounds.map((round) => {
                  const roundMarkets = markets.filter((m) => m.roundId === round.id);
                  const isExpanded = expandedRounds.has(round.id);
                  const typeInfo = (typeId) => MARKET_TYPES[Object.keys(MARKET_TYPES).find((k) => MARKET_TYPES[k].id === typeId)];

                  return (
                    <div key={round.id} className="admin-round-item">
                      <div className="admin-round-info" onClick={() => toggleRound(round.id)} style={{ cursor: 'pointer' }}>
                        <span className={`admin-round-expand ${isExpanded ? 'admin-round-expand--open' : ''}`}>▶</span>
                        <span className="admin-round-number">R{round.roundNumber}</span>
                        <div style={{ flex: 1 }}>
                          <strong>{round.name}</strong>
                          <span className="admin-round-meta">{roundMarkets.length} markets</span>
                        </div>
                        <span className={`admin-round-status admin-round-status--${round.status}`}>{round.status}</span>
                      </div>

                      {/* Round actions */}
                      <div className="admin-round-actions">
                        {['closed', 'concluded'].includes(round.status) && <button className="admin-action-btn admin-action-btn--open" onClick={() => handleRoundAction('reopen', round.id)} disabled={acting !== null}>Reopen round</button>}
                        {isActive && round.status === 'upcoming' && (
                          <button className="admin-action-btn admin-action-btn--open" onClick={() => handleRoundAction('open', round.id)} disabled={acting === round.id}>Open</button>
                        )}
                        {isActive && round.status === 'open' && (
                          <button className="admin-action-btn admin-action-btn--close" onClick={() => handleRoundAction('close', round.id)} disabled={acting === round.id}>Close</button>
                        )}
                        {isActive && (round.status === 'open' || round.status === 'closed') && (
                          <>
                            <button className="admin-action-btn admin-action-btn--conclude" onClick={() => handleRoundAction('conclude-carry', round.id)} disabled={acting === round.id}>Conclude (carry)</button>
                            <button className="admin-action-btn admin-action-btn--conclude-reset" onClick={() => handleRoundAction('conclude-reset', round.id)} disabled={acting === round.id}>Conclude (reset)</button>
                          </>
                        )}
                        <button className="admin-delete-btn-sm" onClick={() => handleDeleteRound(round.id, round.name)} disabled={acting === round.id} title="Delete round">🗑️</button>
                      </div>

                      {round.status === 'concluded' && (
                        <span className="admin-round-concluded-note">{round.carryOver === false ? 'Credits were reset' : 'Credits carried over'}</span>
                      )}

                      {/* Expanded markets */}
                      {isExpanded && (
                        <div className="admin-round-markets">
                          {roundMarkets.length === 0 ? (
                            <p className="admin-round-markets-empty">No markets in this round yet.</p>
                          ) : (
                            roundMarkets.map((mkt) => {
                              const ti = typeInfo(mkt.type);
                              return (
                                <div key={mkt.id} className={`admin-round-mkt admin-round-mkt--${mkt.status}`}>
                                  <div className="admin-round-mkt-info">
                                    <span className="admin-round-mkt-icon">{ti?.icon || '⚽'}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div className="admin-round-mkt-name">{mkt.matchName}</div>
                                      <div className="admin-round-mkt-type">{ti?.name} — <span className={`admin-round-mkt-status admin-round-mkt-status--${mkt.status}`}>{mkt.status}</span>{mkt.winningOption && <> — Winner: <strong>{mkt.winningOption}</strong></>}</div>
                                    </div>
                                    <div className="admin-round-mkt-odds">
                                      {mkt.options.map((o) => <span key={o.name} className="admin-round-mkt-odd">{o.name} <strong>{o.odds}</strong></span>)}
                                    </div>
                                  </div>
                                  {mkt.status !== 'settled' && (
                                    <div className="admin-round-mkt-actions">
                                      {mkt.status === 'open' && <button className="admin-action-btn admin-action-btn--close" onClick={() => act(() => apiService.closeMarket(mkt.id))} disabled={acting !== null}>🔒</button>}
                                      {mkt.status === 'closed' && <button className="admin-action-btn admin-action-btn--open" onClick={() => handleReopenMarket(mkt)} disabled={acting !== null}>Reopen market</button>}
                                      {mkt.options.map((o) => (
                                        <button key={o.name} className="admin-settle-btn" onClick={() => handleSettleMarket(mkt.id, o.name)} disabled={acting === mkt.id} title={`Settle: ${o.name}`}>{o.name}</button>
                                      ))}
                                      <button className="admin-delete-btn-sm" onClick={() => handleDeleteMarket(mkt.id, mkt.matchName)} disabled={acting === mkt.id}>🗑️</button>
                                    </div>
                                  )}
                                  {mkt.status === 'settled' && <button className="admin-action-btn admin-action-btn--open" onClick={() => handleReopenMarket(mkt)} disabled={acting !== null}>Reopen market</button>}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {isActive && (
                <div style={{ marginTop: 12 }}>
                  {addingRoundFor === event.id ? (
                    <div className="admin-inline-form">
                      <input type="text" value={newRoundName} onChange={(e) => setNewRoundName(e.target.value)} placeholder="Extra round name" className="admin-inline-input" />
                      <button className="admin-action-btn admin-action-btn--open" onClick={() => handleCreateRound(event.id)} disabled={!newRoundName.trim()}>Add</button>
                      <button className="admin-action-btn" onClick={() => setAddingRoundFor(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="admin-add-round-btn" onClick={() => setAddingRoundFor(event.id)}>+ Add Extra Round</button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ==================== MARKETS TAB ====================

function MarketsTab() {
  const { api: apiService, events, rounds, refreshData } = useBetting();
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [marketError, setMarketError] = useState('');
  const [addingMarket, setAddingMarket] = useState(false);

  // Custom market builder
  const [showCustom, setShowCustom] = useState(false);
  const [customMatch, setCustomMatch] = useState('');
  const [customMatchDate, setCustomMatchDate] = useState('');
  const [customComp, setCustomComp] = useState('sp');
  const [customType, setCustomType] = useState('unorthodox');
  const [customOptions, setCustomOptions] = useState([{ name: '', odds: '' }, { name: '', odds: '' }]);

  // Event/Round picker
  const activeEvents = events.filter((e) => e.status === 'active');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedRoundId, setSelectedRoundId] = useState('');

  useEffect(() => {
    if (!selectedEventId && activeEvents.length > 0) setSelectedEventId(activeEvents[0].id);
  }, [activeEvents, selectedEventId]);

  const availableRounds = useMemo(() => {
    return rounds.filter((r) => r.eventId === selectedEventId && r.status !== 'concluded').sort((a, b) => a.roundNumber - b.roundNumber);
  }, [rounds, selectedEventId]);

  useEffect(() => {
    const open = availableRounds.find((r) => r.status === 'open');
    setSelectedRoundId(open?.id || availableRounds[0]?.id || '');
  }, [availableRounds]);

  const targetRound = rounds.find((r) => r.id === selectedRoundId);
  const targetEvent = events.find((e) => e.id === selectedEventId);

  const fetchSuggestions = useCallback(async () => {
    setLoadingSugg(true);
    try { setSuggestions(await apiService.getSuggestedMarkets()); }
    catch (err) { setMarketError(err.message || 'Could not load live fixtures'); }
    finally { setLoadingSugg(false); }
  }, [apiService]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const handleAddSuggestion = sug => {
    setCustomMatch(sug.matchName); setCustomComp(sug.competition); setCustomType(sug.type);
    const date = sug.matchDate ? new Date(sug.matchDate) : null;
    setCustomMatchDate(date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');
    setCustomOptions(sug.options.map(o => ({ name: o.name, odds: '' })));
    setShowCustom(true); setMarketError('');
  };

  const handleAddCustom = async () => {
    if (!customMatch.trim() || !selectedRoundId) return;
    const opts = customOptions.filter((o) => o.name.trim() && o.odds);
    if (opts.length < 2 || opts.some(o => !Number.isFinite(Number(o.odds)) || Number(o.odds) < 1)) { setMarketError('Enter at least two options with odds of 1 or more.'); return; }
    setAddingMarket(true); setMarketError('');
    try {
      await apiService.createMarket({
      roundId: selectedRoundId, competition: customComp, matchName: customMatch.trim(),
      matchDate: customMatchDate ? new Date(customMatchDate).toISOString() : null, type: customType,
      options: opts.map((o) => ({ name: o.name.trim(), odds: Number(o.odds) })),
      status: targetRound?.status === 'open' ? 'open' : 'upcoming',
    });
      setCustomMatch(''); setCustomMatchDate(''); setCustomOptions([{ name: '', odds: '' }, { name: '', odds: '' }]); setShowCustom(false);
      await refreshData();
    } catch (err) { setMarketError(err.message || 'Could not create market'); }
    finally { setAddingMarket(false); }
  };

  const updateOption = (idx, field, value) => {
    setCustomOptions((prev) => { const next = [...prev]; next[idx] = { ...next[idx], [field]: value }; return next; });
  };

  const addOption = () => setCustomOptions((prev) => [...prev, { name: '', odds: '' }]);
  const removeOption = (idx) => setCustomOptions((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div>
      {/* Event/Round Picker */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <h3>Target Round</h3>
        <p className="admin-card-sub">Markets will be added to this event & round.</p>
        <div className="admin-picker-row">
          <div className="admin-form-field">
            <label>Event</label>
            <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} className="admin-select">
              {activeEvents.length === 0 && <option value="">No active events</option>}
              {activeEvents.map((evt) => <option key={evt.id} value={evt.id}>{evt.name}</option>)}
            </select>
          </div>
          <div className="admin-form-field">
            <label>Round</label>
            <select value={selectedRoundId} onChange={(e) => setSelectedRoundId(e.target.value)} className="admin-select">
              {availableRounds.length === 0 && <option value="">No rounds available</option>}
              {availableRounds.map((rnd) => <option key={rnd.id} value={rnd.id}>R{rnd.roundNumber}: {rnd.name} ({rnd.status})</option>)}
            </select>
          </div>
        </div>
        {targetEvent && targetRound && (
          <div className="admin-picker-summary">
            <strong>{targetEvent.name}</strong> → <strong>{targetRound.name}</strong>
            <span className={`admin-round-status admin-round-status--${targetRound.status}`} style={{ marginLeft: 8 }}>{targetRound.status}</span>
          </div>
        )}
      </div>

      {/* Custom Market Builder */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        {marketError && <p role="alert" className="admin-error">{marketError}</p>}
        <div className="admin-card-header">
          <h3>Custom Market</h3>
          <button className="admin-refresh-btn" onClick={() => setShowCustom(!showCustom)}>
            {showCustom ? '✕ Cancel' : '+ Create Custom'}
          </button>
        </div>
        <p className="admin-card-sub">Build any market with custom odds — unorthodox markets, specials, whatever you want.</p>

        {showCustom && (
          <div className="admin-custom-builder">
            <div className="admin-form-grid">
              <div className="admin-form-field">
                <label>Market Name</label>
                <input type="text" value={customMatch} onChange={(e) => setCustomMatch(e.target.value)} placeholder="e.g. Both managers get a red card" />
              </div>
              <div className="admin-form-field">
                <label htmlFor="custom-market-deadline">Betting deadline (optional)</label>
                <HelpTip label="Betting deadline">Bets close automatically at this time (usually kick-off). Leave it empty to keep the market open until you close it.</HelpTip>
                <input id="custom-market-deadline" type="datetime-local" value={customMatchDate} onChange={e => setCustomMatchDate(e.target.value)} />
              </div>
              <div className="admin-form-field">
                <label>Competition</label>
                <select value={customComp} onChange={(e) => setCustomComp(e.target.value)} className="admin-select">
                  {Object.values(COMPETITIONS).map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                </select>
              </div>
              <div className="admin-form-field">
                <label>Market Type</label>
                <select value={customType} onChange={(e) => setCustomType(e.target.value)} className="admin-select">
                  {Object.values(MARKET_TYPES).map((t) => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
                </select>
              </div>
            </div>

            <h4 className="admin-section-title" style={{ marginTop: 14 }}>Options & Odds<HelpTip label="Options and odds">Decimal odds, at least 1. A winning stake pays stake × odds, so 2.0 doubles it. Settle the market with the winning option afterwards to pay out.</HelpTip></h4>
            <div className="admin-custom-options">
              {customOptions.map((opt, i) => (
                <div key={i} className="admin-custom-option-row">
                  <input type="text" value={opt.name} onChange={(e) => updateOption(i, 'name', e.target.value)} placeholder={`Option ${i + 1} name`} className="admin-custom-option-name" />
                  <input type="number" value={opt.odds} onChange={(e) => updateOption(i, 'odds', e.target.value)} placeholder="Odds" step="0.1" min="1" className="admin-custom-option-odds" />
                  {customOptions.length > 2 && (
                    <button className="admin-custom-option-remove" onClick={() => removeOption(i)}>✕</button>
                  )}
                </div>
              ))}
              <button className="admin-add-round-btn" onClick={addOption} style={{ marginTop: 6 }}>+ Add Option</button>
            </div>

            <button className="admin-primary-btn" onClick={handleAddCustom} disabled={addingMarket || !customMatch.trim() || !selectedRoundId}>
              {addingMarket ? 'Saving market...' : 'Create Market'}
            </button>
          </div>
        )}
      </div>

      {/* Suggested Markets */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Suggested Markets</h3>
          <button className="admin-refresh-btn" onClick={fetchSuggestions} disabled={loadingSugg}>
            {loadingSugg ? '⏳ Fetching...' : '🔄 Refresh'}
          </button>
        </div>
        <p className="admin-card-sub">Live football fixtures. Select one, set your own odds, and create a market for the chosen round.</p>
        {loadingSugg ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div><p>Scanning for markets...</p></div>
        ) : (
          <div className="admin-suggestions">
            {suggestions.map((sug, idx) => {
              const comp = COMPETITIONS[Object.keys(COMPETITIONS).find((k) => COMPETITIONS[k].id === sug.competition)];
              const typeInfo = MARKET_TYPES[Object.keys(MARKET_TYPES).find((k) => MARKET_TYPES[k].id === sug.type)];
              return (
                <div key={idx} className="admin-suggestion">
                  <div className="admin-suggestion-header">
                    <span className="admin-suggestion-comp" style={{ color: comp?.color }}>{comp?.emoji} {comp?.name}</span>
                    <span className="admin-suggestion-type">{typeInfo?.icon} {typeInfo?.name}</span>
                  </div>
                  <h4 className="admin-suggestion-match">{sug.matchName}</h4>
                  <p className="admin-suggestion-info">{sug.suggestion}</p>
                  <div className="admin-suggestion-odds">
                    {sug.options.map((opt) => <span key={opt.name} className="admin-suggestion-odd">{opt.name}</span>)}
                  </div>
                  <button className="admin-add-btn" onClick={() => handleAddSuggestion(sug)} disabled={!selectedRoundId}>
                    Set odds & create
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== SETTINGS TAB ====================

function SettingsTab() {
  const { api: apiService, settings, updateSettings, refreshData } = useBetting();
  const { activities, masterEvent, updateActivityLocal } = useMasterEvent();
  const { actId } = useParams();
  const activity = activities.find(a => a.id === actId);
  const obfuscated = isActivityObfuscated(activity, masterEvent);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [error, setError] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [credited, setCredited] = useState(false);

  const toggleVisibility = async () => {
    setSavingVisibility(true); setError('');
    try { await updateActivityLocal(actId, { obfuscated: !obfuscated }); }
    catch (err) { setError(err.message || 'Could not update visibility'); }
    finally { setSavingVisibility(false); }
  };

  const handleCredit = async () => {
    const val = parseInt(creditAmount, 10);
    if (isNaN(val) || val <= 0) return;
    await apiService.creditAllUsers(val);
    setCredited(true); setCreditAmount('');
    await refreshData();
    setTimeout(() => setCredited(false), 3000);
  };

  return (
    <div>
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <h3>Game Settings</h3>
        {error && <p role="alert" className="admin-error">{error}</p>}
        <p className="admin-card-sub">Control how the game works for all players.</p>
        <div className="admin-toggle-list">
          <div className="admin-toggle-row">
            <div className="admin-toggle-info">
              <span className="admin-toggle-label">Obfuscate Betting Activity<HelpTip label="Obfuscate betting activity">Same switch as the activity's Obfuscate setting. Members see only the hint until you reveal it, and the server refuses their betting requests meanwhile.</HelpTip></span>
              <span className="admin-toggle-desc">Members cannot enter or view markets and picks until revealed. Admins keep full access.</span>
            </div>
            <button type="button" role="switch" aria-label="Obfuscate betting activity" aria-checked={obfuscated} disabled={savingVisibility} className={`admin-toggle ${obfuscated ? 'admin-toggle--on' : ''}`} onClick={toggleVisibility}><span className="admin-toggle-knob"></span></button>
          </div>
          <div className="admin-toggle-row">
            <div className="admin-toggle-info">
              <span className="admin-toggle-label">Allow Bet Replacement<HelpTip label="Allow bet replacement">When on, bettors can change or cancel a pending bet until the market closes. Cancelling refunds the stake.</HelpTip></span>
              <span className="admin-toggle-desc">Let bettors change or cancel bets while the market is open.</span>
            </div>
            <button className={`admin-toggle ${settings.allowBetReplacement ? 'admin-toggle--on' : ''}`} onClick={() => updateSettings({ allowBetReplacement: !settings.allowBetReplacement })}><span className="admin-toggle-knob"></span></button>
          </div>
        </div>
      </div>
      <div className="admin-card">
        <h3>Credit Event Participants<HelpTip label="Credit everyone">Adds the same amount to every player's (or team's) current balance in the active event. To change a single team, use the Balances tab.</HelpTip></h3>
        <p className="admin-card-sub">Credit this activity's event members or shared team wallets.</p>
        <div className="admin-credit-row">
          <input type="number" className="admin-credit-input" placeholder="Amount" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} min="1" />
          <button className="admin-credit-btn" onClick={handleCredit} disabled={!creditAmount}>💰 Credit Everyone</button>
        </div>
        {credited && <div className="admin-success">Event wallets credited!</div>}
      </div>
    </div>
  );
}

// ==================== BALANCES TAB ====================

function BalancesTab() {
  const { api: apiService, events, standings = [], isTeamBased, refreshData } = useBetting();
  const [amounts, setAmounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const event = events.find((e) => e.status === 'active');
  if (!event) return <p className="admin-empty">Create or reopen a betting event to manage balances.</p>;
  const usesPoints = event.balanceSource === 'points';
  const who = isTeamBased ? 'team' : 'player';

  const run = async (fn, success) => {
    setBusy(true); setError(''); setMessage('');
    try { await fn(); await refreshData(); setMessage(success); return true; }
    catch (err) { setError(err.message); return false; }
    finally { setBusy(false); }
  };
  const adjust = async (row, sign) => {
    const amount = Number(amounts[row.id]);
    if (!Number.isFinite(amount) || amount <= 0) { setError('Enter a positive amount'); return; }
    const done = await run(() => apiService.adjustBalance(row.id, sign * amount), `${sign > 0 ? 'Added' : 'Removed'} ${amount} coins ${sign > 0 ? 'to' : 'from'} ${row.name}.`);
    if (done) setAmounts((prev) => ({ ...prev, [row.id]: '' }));
  };

  return (
    <div>
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <h3>Starting Balance<HelpTip label="Starting balance">Choose what each {who} starts {event.name} with. Switching moves every wallet by the difference, so coins already staked or won are kept.</HelpTip></h3>
        <p className="admin-card-sub">
          {usesPoints
            ? `Each ${who} starts with the points they earned in other competition activities (1 point = 1 coin).`
            : `Each ${who} starts with ${event.startingCredits?.toLocaleString()} credits.`}
        </p>
        <div className="admin-credit-row">
          <button className="admin-credit-btn" disabled={busy || !usesPoints} onClick={() => run(() => apiService.setBalanceSource('credits'), 'Balances now start from the event credits.')}>
            Use starting credits
          </button>
          <button className="admin-credit-btn" disabled={busy} onClick={() => run(() => apiService.setBalanceSource('points'), 'Balances now carry over competition points.')}>
            {usesPoints ? '↻ Refresh competition points' : 'Carry over competition points'}
          </button>
          <HelpTip label="Carry over competition points">Uses the points shown on the Scores page: everything this {who} earned in other activities. Click Refresh after more competitions are scored.</HelpTip>
        </div>
      </div>

      <div className="admin-card">
        <h3>{isTeamBased ? 'Team' : 'Player'} Balances<HelpTip label="Adjusting balances">Add or remove coins for one {who}. Adjustments become part of its base, so they survive round resets. A balance cannot go below zero.</HelpTip></h3>
        {error && <p role="alert" className="admin-error">{error}</p>}
        {message && <div className="admin-success">{message}</div>}
        {standings.length === 0 ? (
          <p className="admin-empty">{isTeamBased ? 'No teams with members yet. Set them up in Team Selection.' : 'No active members yet.'}</p>
        ) : (
          <div className="admin-users-table">
            {standings.map((row) => (
              <div key={row.id} className="admin-user-row-full admin-balance-row">
                <div className="admin-user-details">
                  <div className="admin-user-name-full">{row.name}</div>
                  <div className="admin-user-meta">
                    Base {(row.baseBalance ?? 0).toLocaleString()}
                    {usesPoints && <> · {row.earlierPoints ?? 0} pts carried</>}
                    {!!row.adjustment && <> · {row.adjustment > 0 ? '+' : ''}{row.adjustment} adjusted</>}
                  </div>
                </div>
                <span className="admin-user-balance">{(row.balance ?? 0).toLocaleString()} coins</span>
                <div className="admin-balance-actions">
                  <input
                    type="number" min="1" className="admin-credit-input"
                    aria-label={`Amount for ${row.name}`} placeholder="Amount"
                    value={amounts[row.id] || ''} onChange={(e) => setAmounts({ ...amounts, [row.id]: e.target.value })}
                  />
                  <button className="admin-action-btn admin-action-btn--open" disabled={busy} onClick={() => adjust(row, 1)} aria-label={`Add coins to ${row.name}`}>+ Add</button>
                  <button className="admin-action-btn admin-action-btn--close" disabled={busy} onClick={() => adjust(row, -1)} aria-label={`Remove coins from ${row.name}`}>− Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== USERS TAB ====================

function UsersTab() {
  const { members = [], standings, isTeamBased } = useBetting();
  const { isEventAdmin } = useMasterEvent();
  const { id } = useParams();

  return (
    <div>
      <div className="admin-card-header" style={{ marginBottom: 16 }}>
        <h3>Event Members</h3>
        {isEventAdmin && <Link className="admin-refresh-btn" to={`/me/${id}/admin`}>Manage event members and teams</Link>}
      </div>
      <p className="admin-card-sub">Only members of this event are listed. {isTeamBased ? 'Bets use shared team wallets; personal coins cannot be spent here.' : 'Balances belong to this betting activity.'}</p>
      <div className="admin-card">
        <div className="admin-users-table">
          {members.map((user) => (
            <div key={user.id} className="admin-user-row-full">
              <Avatar user={user} size={18} className="admin-user-avatar" />
              <div className="admin-user-details">
                <div className="admin-user-name-full">{user.displayName}</div>
                <div className="admin-user-meta">{user.role} · {user.status}{isTeamBased && <> · {user.teamName || 'No team assigned'}</>}</div>
              </div>
              <span className="admin-user-balance">{isTeamBased
                ? (user.teamId ? `${(standings.find(s => s.teamId === user.teamId)?.balance ?? 0).toLocaleString()} team coins` : 'Assign a team to bet')
                : `${(user.balance ?? 0).toLocaleString()} coins`}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN ====================

const TABS = [
  { key: 'events', label: 'Events & Rounds', icon: '📅' },
  { key: 'markets', label: 'Markets', icon: '🎯' },
  { key: 'settings', label: 'Settings', icon: '⚙️' },
  { key: 'balances', label: 'Balances', icon: '💰' },
  { key: 'users', label: 'Users', icon: '👥' },
];

export default function Admin() {
  const [activeTab, setActiveTab] = useState('events');
  const { isActivityAdmin } = useMasterEvent();
  if (!isActivityAdmin) return <p>Only activity admins can manage betting.</p>;
  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <p className="admin-header-sub">Manage betting events & markets</p>
      </div>
      <div className="admin-tabs">
        {TABS.map((tab) => (
          <button key={tab.key} className={`admin-tab ${activeTab === tab.key ? 'admin-tab--active' : ''}`} onClick={() => setActiveTab(tab.key)}>
            <span className="admin-tab-icon">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>
      <div className="admin-tab-content">
        {activeTab === 'events' && <EventsTab />}
        {activeTab === 'markets' && <MarketsTab />}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'balances' && <BalancesTab />}
        {activeTab === 'users' && <UsersTab />}
      </div>
    </div>
  );
}
